import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table } from 'react-bootstrap'
import Form from 'react-bootstrap/Form'
import { 
  Paper,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Skeleton,
  Box,
  Button,
  Typography
} from '@mui/material'

import { post, put } from '../utils/http'
import { User } from '../utils/types'

const AdminPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [userList, setUserList] = useState<User[]>([])
  // const [checkedList, setCheckedList] = useState<number[]>([])
  const [page, setPage] = useState<number>(0)
  const [total, setTotal] = useState<number>(0)

  const navigate = useNavigate()
  const maxUploadLimit = 9999999
  const rowCount = 100
  const adminId = Number(localStorage.getItem('adminId') || '')
  const userRole = localStorage.getItem('role')
  const isAdmin = ['admin', 'dev', 'superadmin'].includes(userRole || 'null')
  const isSuperAdmin = ['dev', 'superadmin'].includes(userRole || 'null')
  const currentUser = localStorage.getItem('user_email')
  // const isDev = ['dev'].includes(userRole || 'null') 
  // const devId = Number(localStorage.getItem('devId') || '')

  const userHandler = (id: number ) => {
    localStorage.setItem('user_id', String(id))
    navigate('/dashboard', { replace: true })
  }

  const userActiveStatusHandler = async (userId: number, activeStatus: boolean) => {
    try {
      await put(`Users/updatestatus`, {
        admin_id: adminId,
        user_data: { id: userId, active: activeStatus }
      })
    } catch (err) {
      console.log('userlist', err)
    }
  }

  const handleActivationStatus = (event: any, id: number) => {
    const userData = userList.filter((item: User) => item.id === id)
    userData.map((item: User) => {
      if (event.target.checked === true) item.active = true
      else item.active = false
      return item
    })
    // event.target.checked ? setCheckedList(prevState => [...prevState, id]) : setCheckedList(prevState => prevState.filter((temp) => temp !== id))
    userActiveStatusHandler(id, event.target.checked)
  }

  const userAdminStatusHandler = async (userId: number, adminStatus: boolean) => {
    try {
      await put(`Users/updateadminstatus`, {
        admin_id: adminId,
        user_data: { id: userId, admin: adminStatus }
      })
    } catch (err) {
      console.log('updateadminstatus', err)
    }
  }

  const handleAdminStatus = (event: any, id: number) => {
    const userData = userList.filter((item: User) => item.id === id)
    userData.map((item: User) => {
      if (event.target.checked === true) item.admin = true
      else item.admin = false
      return item
    })
    // event.target.checked ? setCheckedList(prevState => [...prevState, id]) : setCheckedList(prevState => prevState.filter((temp) => temp !== id))
    userAdminStatusHandler(id, event.target.checked)
  }

  const handleInfinitUploadLimit = (userId: number) => {
    const input = document.querySelector(`#usr_uplimit_inpt_${userId}`)! as HTMLInputElement
    input.value = String(maxUploadLimit)
    updateUploadLimit(userId)
  }

  const updateUserData = async (userId: number, uploadLimit: string) => {
    const postUserData = {
      admin_id: adminId,
      user_data: { id: userId, upload_limit: uploadLimit }
    }
    await put(`Users/updateuserdata`, postUserData)
  }

  const updateUploadLimit = async (userId: number) => {
    const input = document.querySelector(`#usr_uplimit_inpt_${userId}`)! as HTMLInputElement
    await updateUserData(userId, input.value)
  }

  const handleChangePage = (event: any, newPage: any) => setPage(newPage)

  const fetchUserList = async () => {
    setIsLoading(true)
    try {
      const res = await post(`Users/list-users-by-page`, { page: page, rows: rowCount })
      setUserList(res.users)
      setTotal(res.total)
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUserList()
  }, [page])  // eslint-disable-line

  return (
    <Box className="d-flex flex-column align-items-center p-3 w-100" sx={{maxWidth: 1200, margin: '0 auto'}}>
      <Paper elevation={3} className='w-100'>
        <Box className='p-3'>
          <Box className="d-flex justify-content-between gap-3 flex-wrap">
            <Typography variant='h5' className='mb-4'>User Accounts</Typography>
            <TablePagination
              component='div'
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowCount}
              onRowsPerPageChange={() => {}}
              rowsPerPageOptions={[rowCount]}
            />
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  {isSuperAdmin && <TableCell>Upload Limit</TableCell>}
                  <TableCell>User Directory</TableCell>
                  {isAdmin && <TableCell>Active</TableCell>}
                  {isSuperAdmin && <TableCell>Admin</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 3 }, (_, i) => i + 1).map(i => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>
                      <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>
                      {isSuperAdmin && <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>}
                      <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>
                      {isAdmin && <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>}
                      {isSuperAdmin && <TableCell><Skeleton variant='rectangular' height={40}/></TableCell>}
                    </TableRow>
                  ))
                  : userList.map(i => (
                    <TableRow key={i.id}>
                      <TableCell>{i.id}</TableCell>
                      <TableCell>{i.email}</TableCell>
                      {isSuperAdmin && <TableCell>
                        <Form className='d-flex gap-3'>
                          <Form.Control
                            id={`usr_uplimit_inpt_${i.id}`}
                            type="number"
                            min={1}
                            max={maxUploadLimit}
                            defaultValue={i.upload_limit}
                            onChange={() => updateUploadLimit(i.id)}
                            style={{ width: "105px" }}
                          />
                          <button
                            className="btn btn-sm btn-link p-0 fs-4 text-decoration-none"
                            onClick={() => handleInfinitUploadLimit(i.id)}
                          >
                            &infin;
                          </button>
                        </Form>
                      </TableCell>}
                      <TableCell>
                        <Button onClick={() => userHandler(i.id)}>view</Button>
                      </TableCell>
                      {isAdmin && <TableCell>
                        <Form>
                          <Form.Check
                            type="switch"
                            defaultChecked={i.active}
                            onChange={(e) => handleActivationStatus(e, i.id)}
                            disabled={currentUser === i.email || ["superadmin", "dev"].includes(i.role) ? true : false}
                          />
                        </Form>
                      </TableCell>}
                      {isSuperAdmin && <TableCell>
                        <Form>
                          <Form.Check
                            type="switch"
                            defaultChecked={i.admin}
                            onChange={(e) => handleAdminStatus(e, i.id)}
                            disabled={currentUser === i.email || ["superadmin", "dev"].includes(i.role) ? true : false}
                          />
                        </Form>
                      </TableCell>}
                    </TableRow>
                  ))
                } 
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowCount}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[rowCount]}
          />
        </Box>
      </Paper>
    </Box>
  )
}

export default AdminPage
